import { useFindMany } from "@gadgetinc/react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../api";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { SegmentFiltersTooltip } from "../../../externalComponents/SegmentFiltersTooltip";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import { Link } from "../../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { Text } from "../../../externalComponents/tailwind/text";

export default function OngoingSendings({ user, isMobile }) {
  const navigate = useNavigate();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [stoppingBatchIds, setStoppingBatchIds] = useState(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStop, setPendingStop] = useState(null);
  const [visibleStoppedIds, setVisibleStoppedIds] = useState(new Set());
  const previousStatusRef = useRef(new Map());

  const cutoffDate = new Date("2026-02-10T00:00:00.000Z");

  const [{ data: allLiveSendings, fetching }] = useFindMany(api.sending, {
    live: true,
    filter: {
      customerId: { equals: user.customerId },
      OR: [
        { status: { equals: "ongoing" } },
        { status: { equals: "stopped" } },
      ],
      sendingDate: { greaterThanOrEqual: cutoffDate },
      sendingBatchId: { isSet: true },
    },
    select: {
      id: true,
      contactList: true,
      sendingBatchId: true,
      sendingDate: true,
      campaignId: true,
      status: true,
      contactListSegment: {
        externalId: true,
        type: true,
      },
      segment: {
        id: true,
        name: true,
      },
      segmentFilters: true,
      campaign: {
        id: true,
        name: true,
        automationType: true,
      },
    },
    sort: {
      sendingDate: "Descending",
    },
  });

  const [{ data: stoppedSendingsData }] = useFindMany(api.sendingStopped, {
    live: true,
    filter: {
      customerId: { equals: user.customerId },
    },
    select: {
      id: true,
      sendingBatchId: true,
      campaignId: true,
    },
  });

  const handleStopClick = (campaignId, sendingBatchId, campaignName) => {
    setPendingStop({ campaignId, sendingBatchId, campaignName });
    setShowConfirmDialog(true);
  };

  const handleConfirmStop = async () => {
    if (!pendingStop) return;

    const { campaignId, sendingBatchId } = pendingStop;
    setStoppingBatchIds((prev) => new Set(prev).add(sendingBatchId));
    setShowConfirmDialog(false);
    setPendingStop(null);

    try {
      await api.stopProcessingCampaign({
        customerId: user.customerId,
        campaignId: `${campaignId}`,
        sendingBatchId,
      });
    } catch (error) {
      console.error("Error stopping campaign:", error);
    } finally {
      setStoppingBatchIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sendingBatchId);
        return newSet;
      });
    }
  };

  const handleCancelStop = () => {
    setShowConfirmDialog(false);
    setPendingStop(null);
  };

  useEffect(() => {
    if (!allLiveSendings) return;

    allLiveSendings.forEach((sending) => {
      if (sending.status === "ongoing") {
        setVisibleStoppedIds((prev) => {
          if (prev.has(sending.id)) return prev;
          return new Set(prev).add(sending.id);
        });
      }

      if (sending.status === "stopped") {
        setVisibleStoppedIds((prev) => {
          if (!prev.has(sending.id)) return prev;
          previousStatusRef.current.set(sending.id, "stopped");
          return prev;
        });
      }
    });
  }, [allLiveSendings]);

  const displayedSendings = (allLiveSendings || []).filter((sending) => {
    if (sending.status === "ongoing") {
      return true;
    }
    if (sending.status === "stopped" && visibleStoppedIds.has(sending.id)) {
      return true;
    }
    return false;
  });

  const stoppedBatchIds = new Set(
    stoppedSendingsData?.map((s) => s.sendingBatchId) || [],
  );

  const rowMarkup = displayedSendings?.map((sending, index) => {
    const sendingDate = sending.sendingDate
      ? new Date(sending.sendingDate)
      : null;

    const isAlreadyStopped = sending.status === "stopped";
    const hasStopRequest = stoppedBatchIds.has(sending.sendingBatchId);
    const shouldShowStopButton =
      sending.sendingBatchId && !isAlreadyStopped && !hasStopRequest;

    const showSegmentColumn = !sending.campaign?.automationType;

    return (
      <TableRow
        key={`ongoing-${sending.id}`}
        position={index}
        {...(!isMobile && {
          onClick: () => navigate(`/campaigns/edit/${sending.campaign?.id}`),
        })}
      >
        <TableCell className="font-bold">{sending.campaign?.name}</TableCell>
        <TableCell>
          {sendingDate
            ? sendingDate.toLocaleDateString("fr-FR", {
                weekday: "long",
                month: "long",
                year: "numeric",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              }) + ` [${timezone}]`
            : "-"}
        </TableCell>
        <TableCell className="text-center">
          {sending.contactList.length}
        </TableCell>
        {showSegmentColumn && (
          <TableCell className="w-[30%]">
            {sending.segment ? (
              <SegmentFiltersTooltip segmentFilters={sending.segmentFilters}>
                <span className="cursor-help">{sending.segment.name}</span>
              </SegmentFiltersTooltip>
            ) : sending.contactListSegment?.type === "klaviyo-segment" ? (
              <Link
                style={{
                  wordBreak: "break-all",
                  overflowWrap: "break-word",
                }}
                className="hover:underline"
                href={`https://www.klaviyo.com/list/${sending.contactListSegment?.externalId}`}
                target="_blank"
              >
                {`https://www.klaviyo.com/list/${sending.contactListSegment?.externalId}`}
              </Link>
            ) : (
              "-"
            )}
          </TableCell>
        )}
        <TableCell className="text-center font-bold">
          {sending.status}
        </TableCell>

        {!isMobile && (
          <TableCell className="text-center">
            {shouldShowStopButton ? (
              <Button
                color="secondaryButton"
                className="min-w-[100px] h-10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStopClick(
                    sending.campaign?.id,
                    sending.sendingBatchId,
                    sending.campaign?.name,
                  );
                }}
                disabled={stoppingBatchIds.has(sending.sendingBatchId)}
              >
                {stoppingBatchIds.has(sending.sendingBatchId)
                  ? "Stopping..."
                  : "Stop"}
              </Button>
            ) : (
              <span className="text-gray-400">
                {!isAlreadyStopped && hasStopRequest ? "Stop requested" : "-"}
              </span>
            )}
          </TableCell>
        )}
      </TableRow>
    );
  });

  if (!allLiveSendings && fetching) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <br />
      <Text className="text-xl font-bold">Ongoing messages</Text>
      <br />
      {displayedSendings?.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Campaign name</TableHeader>
                <TableHeader>Sending date</TableHeader>
                <TableHeader className="text-center">Recipients</TableHeader>
                <TableHeader className="w-[30%]">Segment</TableHeader>
                <TableHeader className="text-center">Status</TableHeader>
                {!isMobile && (
                  <TableHeader className="text-center">Action</TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>{rowMarkup}</TableBody>
          </Table>
        </>
      ) : (
        <EmptyState heading="There are no on-going messages" />
      )}

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
            <h3 className="text-lg font-bold mb-4">Stop Campaign</h3>
            <p className="text-gray-700 mb-3">
              Are you sure you want to stop this campaign?
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="font-semibold text-sm text-gray-900 mb-2">
                Impact of stopping:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    All <strong>scheduled messages</strong> will be stopped
                    immediately
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    <strong>messages</strong> will be stopped as soon as
                    possible
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    Messages <strong>already sent</strong> will not be affected
                  </span>
                </li>
              </ul>
            </div>
            <div className="flex justify-end gap-3">
              <Button color="secondaryButton" onClick={handleCancelStop}>
                Cancel
              </Button>
              <Button color="red" onClick={handleConfirmStop}>
                Stop Campaign
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
